import { FC, useEffect } from 'react'
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
import { DataGrid } from 'src/v2/components/data-grid'
import { ParcelTypeChip } from 'src/v2/components/parcel-type-chip'
import { PARCEL_ACQUISITION_DATA } from 'src/v2/views/owner-reveal/insights/acquisition-history/acquisitions-overview/data-temp'
import { ParcelChip } from 'src/v2/components/parcel-chip'
import styled from '@emotion/styled'
import { Colors } from 'src/components/theme'
import { FlexRow } from 'src/components/flex-containers'
import { Typography } from '@mui/material'
import { ArrowUp } from '@emotion-icons/heroicons-solid/ArrowUp'
import { ArrowDown } from '@emotion-icons/heroicons-solid/ArrowDown'
import * as atoms from 'src/v2/views/owner-reveal/insights/acquisition-history/acquisitions-overview/temp-atoms'
import { useAtom } from 'jotai'

const Container = styled.div`
  height: 548px;
`

const DEFAULT_COLUMN_SETTINGS = {
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  hideable: false,
  resizable: false,
}

type ActivityItemProps = {
  activity: string
}
const ActivityItem: FC<ActivityItemProps> = ({ activity }) => {
  const isSold = activity === 'Sold'
  const color = isSold ? Colors.primaryRed500 : Colors.primaryGreen600

  return (
    <FlexRow gap={12} align="center">
      {isSold ? <ArrowDown size={14} color={color} /> : <ArrowUp size={14} color={color} />}
      <Typography variant="textmdm" color={color}>
        {activity}
      </Typography>
    </FlexRow>
  )
}

const COLUMNS = (): GridColDef[] => [
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'date',
    headerName: 'Date',
    width: 100,
    align: 'center',
    headerAlign: 'center',
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'name',
    headerName: 'Parcel',
    width: 300,
    renderCell: ({ value }) => <ParcelChip>{value}</ParcelChip>,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'activity',
    headerName: 'Activity',
    width: 120,
    renderCell: ({ value }) => <ActivityItem activity={value} />,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'type',
    headerName: 'Type',
    width: 240,
    renderCell: ({ value }) => {
      return (
        <div>
          <ParcelTypeChip type={value} />
        </div>
      )
    },
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'lotSizeSqFt',
    headerName: 'Lot Size (Sq. Ft.)',
    width: 140,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'lotSizeAcres',
    headerName: 'Lot Size (Acres)',
    width: 140,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'yearBuilt',
    headerName: 'Year Built',
    width: 94,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'numberOfStories',
    headerName: '# of Stories',
    width: 108,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'numberOfBuildings',
    headerName: '# of Buildings',
    width: 124,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'numberOfCommercialUnits',
    headerName: '# of Commercial Units',
    width: 190,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'recentAcquisition',
    headerName: 'Recent Acquisition',
    width: 160,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'recentSalePrice',
    headerName: 'Recent Sale Price',
    width: 150,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'mortgageAmount',
    headerName: 'Mortgage Amount',
    width: 160,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'mortgageOriginationDate',
    headerName: 'Mortgage Origination Date',
    width: 230,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'maturityDate',
    headerName: 'Maturity Date',
    width: 130,
  },
  {
    ...DEFAULT_COLUMN_SETTINGS,
    field: 'apn',
    headerName: `Assessor's Parcel Number (APN)`,
    width: 260,
  },
]

export const AcquisitionsOverviewTable: FC = () => {
  const apiRef = useGridApiRef()
  const [selectedItem, setSelectedId] = useAtom(atoms.selectedItem)

  useEffect(() => {
    if (!selectedItem || selectedItem.origin !== 'map') {
      return
    }

    const rowIndex = PARCEL_ACQUISITION_DATA.findIndex((d) => d.id === selectedItem.id)
    apiRef.current.scrollToIndexes({ rowIndex })
  }, [selectedItem])

  return (
    <Container>
      <DataGrid
        apiRef={apiRef}
        disableVirtualization
        columns={COLUMNS()}
        getRowClassName={(params) => (params.row.id === selectedItem?.id ? 'selected' : '')}
        rows={PARCEL_ACQUISITION_DATA}
        rowHeight={40}
        disableColumnFilter
        disableRowSelectionOnClick
        borderlessHeader
        onRowClick={(params) => {
          setSelectedId({
            id: params.row.id,
            origin: 'table',
          })
        }}
      />
    </Container>
  )
}
